'use client'

import { type ReactNode } from 'react'
import Image from 'next/image'
import { MenuTitle } from '@/components/navigation/SubMenu'
import NavLink from '@/components/ui/NavLink'
import { Icon } from '@/icons/Icon'
import { FeaturedPlacement, trackFeaturedClick } from '@/lib/analytics'
import { useDiscoverMenuContext, type AppType } from '@/context/NavbarContext'
import AppIcon from '@/components/content/app/AppIcon'
import { getBrandColors, hexToRgba } from '@/lib/utils'
import type { Media } from '@/payload-types'

type CategoryProps = {
  title: string
  text: string
  link: string
  icon: ReactNode
  apps: AppType[]
}

type ExploreLinkProps = {
  link: string
  icon: string
  total: number
  label: string
}

type FeaturedReviewProps = {
  app: AppType
}

export default function DiscoverMenu() {
  const { bestApps = [], reviewsApps = [], alternativesApps = [] } = useDiscoverMenuContext()
  const featuredApp = bestApps[0] ?? reviewsApps[0]

  const recommended = [
    {
      title: 'The best apps',
      text: 'Discover top tools across categories for every use case',
      link: '/best',
      icon: <Icon name="best" />,
      apps: bestApps,
    },
    {
      title: 'App reviews',
      text: 'Deep dives into performance, features, and usability.',
      link: '/apps',
      icon: <Icon name="review" size={12} />,
      apps: reviewsApps,
    },
    {
      title: 'App alternatives',
      text: 'Compare similar apps to find the perfect fit for you.',
      link: '/alternatives',
      icon: <Icon name="alternative" size={12} />,
      apps: alternativesApps,
    },
  ]

  return (
    <div
      data-menu="Discover"
      className="flex w-full shrink-0 flex-col divide-gray-200 dark:divide-white/10 sm:flex-row sm:divide-x navigation:w-[785px]"
    >
      <div className="flex w-full flex-col gap-2 px-2 py-5 sm:w-2/5">
        <MenuTitle title="Recommended" />
        <div className="flex flex-col sm:gap-5">
          {recommended.map((category, index) => (
            <Recommended
              key={index}
              title={category.title}
              text={category.text}
              link={category.link}
              icon={category.icon}
              apps={category.apps}
            />
          ))}
        </div>
      </div>
      <div className="flex w-full flex-col gap-8 pb-5 sm:w-3/5 navigation:pt-5">
        <FeaturedReview app={featuredApp} />
        <Explore />
      </div>
    </div>
  )
}

export function Recommended({ title, text, link, icon, apps }: CategoryProps) {
  return (
    <NavLink
      href={link}
      className="group flex cursor-pointer select-none items-center gap-6 rounded-xl py-2.5 pl-5 pr-6 transition-colors duration-300 hover:bg-gray-100 dark:hover:bg-white/10"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-950 transition-colors duration-300 group-hover:text-primary-500 dark:border-white/10 dark:bg-black dark:text-white">
        {icon}
      </div>

      <div className="flex flex-col gap-2">
        <span className="pt-1 text-14 font-medium leading-none text-gray-950 transition-colors duration-300 group-hover:text-primary-500 dark:text-white">
          {title}
        </span>
        <p className="text-12 font-medium leading-normal text-zinc-500">{text}</p>
        {apps.length ? (
          <div className="flex gap-2 pb-2 pt-1.5">
            {apps.map((app) => (
              <AppIcon
                key={app.id}
                icon={app.icon}
                glyph={app.glyph}
                name={app.name}
                background={app.brandBackgroundColor}
                size={20}
              />
            ))}
          </div>
        ) : null}
      </div>
    </NavLink>
  )
}

export function FeaturedReview({ app }: FeaturedReviewProps) {
  if (!app) {
    return null
  }

  const homepageImage = app.homepageImage ? (app.homepageImage as Media) : null
  const homepageImageUrl = homepageImage?.url

  const { primaryColor, backgroundColor } = getBrandColors(app)

  return (
    <div className="flex flex-col gap-2 px-2">
      <MenuTitle title="What's New" />
      <NavLink
        href={`/apps/${app.slug}`}
        onClick={() => trackFeaturedClick(FeaturedPlacement.NAV_DISCOVER, app.name)}
        className="group flex flex-col items-start gap-4 px-5"
      >
        <div
          className="relative mb-1 mt-2.5 h-[180px] w-full overflow-hidden rounded-xl bg-black dark:bg-white/5"
          style={{
            background: `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor, 0.25)} 0%, ${hexToRgba(primaryColor, 0)} 80%), linear-gradient(180deg, rgba(0, 0, 0, 0) 66.66%, rgba(0, 0, 0, 0.5) 90%), ${backgroundColor}`,
          }}
        >
          {/* Same CSS-bg → next/image swap as ReviewThumbnail's ThumbnailSecondary
              case. The R2-hosted homepage PNGs are ~145-320 KB raw; routing
              through /_next/image gets them to ~10-15 KB AVIF. `object-left-top`
              matches CSS background-image's top-left anchor (next/image's
              `object-cover` otherwise center-anchors and silently re-crops). */}
          <div className="backface-hidden absolute -bottom-1/3 aspect-screen w-3/4 translate-x-24 -rotate-[15deg] skew-x-12 skew-y-3 overflow-hidden rounded-xl bg-white shadow-[0px_50px_100px_-25px_rgba(0,0,0,1)] transition-transform duration-1000 ease-in-out after:absolute after:inset-0 after:rounded-xl after:border after:border-white after:border-opacity-15 group-hover:translate-x-16">
            {homepageImageUrl && (
              <Image
                src={homepageImageUrl}
                alt={`${app.name} homepage screenshot`}
                fill
                sizes="320px"
                className="object-cover object-left-top"
              />
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <span className="text-14 font-medium leading-none text-gray-950 dark:text-white">
            App review: {app.name}
          </span>
          <p className="text-12 font-medium leading-normal text-zinc-500">{app.description}</p>
        </div>
        <div className="flex items-center">
          <AppIcon
            key={app.id}
            icon={app.icon}
            glyph={app.glyph}
            name={app.name}
            background={app.brandBackgroundColor}
            size={20}
          />
          <div className="ml-3 rotate-180 text-zinc-500 transition-[margin-left] duration-500 ease-in-out group-hover:ml-5">
            <Icon name="chevron-left" size={12} />
          </div>
        </div>
      </NavLink>
    </div>
  )
}

export function Explore() {
  const { dealCount = 0, stackCount = 0 } = useDiscoverMenuContext()

  return (
    <div className="flex flex-col gap-2 px-2 py-2">
      <MenuTitle title="Explore" />
      <div className="flex items-center gap-10 px-5">
        <ExploreLink link="/deals" icon="deal" total={dealCount} label="deals" />
        <ExploreLink link="/stacks" icon="stack" total={stackCount} label="stacks" />
      </div>
    </div>
  )
}

export function ExploreLink({ link, icon, total, label }: ExploreLinkProps) {
  return (
    <NavLink href={link} className="group flex items-center gap-2.5">
      <Icon name={icon} size={12} className="text-primary-500" />
      <p className="text-13 font-medium leading-none text-zinc-500 transition-colors group-hover:text-gray-950 dark:group-hover:text-white">
        <span className="mr-1 text-gray-950 dark:text-white">{total}</span> {label}
      </p>
    </NavLink>
  )
}
